import api from './api';

// ============================================================
// schedulerHealth — saúde do escalonamento de detectores (FROTA)
// ============================================================
// Endpoint (admin, SUPER_ADMIN):
//   GET /admin/scheduler-health?window_minutes=N
//
// ⚠️ Fora de /admin/diagnostics/... de propósito: um último segmento literal
// ali colidiria com o parâmetro {uuid} de GET /admin/diagnostics/{uuid}.
//
// ⚠️ SEM session_uuid, também de propósito: a pergunta que isto responde
// ("algum detector está passando fome?") é ANTERIOR a suspeitar de uma sessão.
// Exigir um UUID transformaria um sintoma que aparece num sintoma que alguém
// precisa ir procurar — que é exatamente como 4 min e meio de anti-cheat cego
// passaram despercebidos.
//
// ⛔ DIAGNÓSTICO OPERACIONAL, NÃO EVIDÊNCIA. Nada daqui pontua contra jogador,
// vira evento ou entra em família de evidência. Detector lento é problema
// nosso; a máquina lenta é do jogador. Ver lib/detectorHealth.ts.
//
// 🔒 LGPD: o backend só aceita NOME DE DETECTOR neste payload, e a defesa tem
// DOIS níveis, com desfechos DIFERENTES — não confunda um com o outro:
//
//   1. Caractere proibido (fora de [A-Za-z0-9_.:-]) — caminho de arquivo,
//      espaço, barra. O upload inteiro é RECUSADO com 400. Ali não é lista
//      desatualizada: é emissor que não é o nosso SDK.
//   2. Nome bem-formado que NÃO está na allowlist de detectores conhecidos —
//      `svchost.exe`, `cheatengine-i386.exe`. A entrada é DESCARTADA (o nome
//      nunca é persistido nem renderizado), os números dela continuam nos
//      totais, e o contador de desconhecidos sobe. O upload é ACEITO.
//
// O nível 2 existe porque charset sozinho NÃO basta: `.` e `-` são legítimos em
// nome de detector e também em nome de processo, então `cheatengine-i386.exe`
// passava inteiro. A primitiva certa aqui é allowlist, não charset — o conjunto
// de nomes válidos é fechado e conhecido, então não se tenta prever o que o
// atacante inventa; aceita-se só o que se conhece.
//
// O descarte NÃO recusa o upload de propósito: um SDK novo, com detector que o
// backend ainda não conhece, precisa continuar reportando. Daí o contador de
// desconhecidos — é o que impede a allowlist de apodrecer em silêncio.
//
// Nenhum campo aqui carrega identificador do que foi varrido.
// ============================================================

// ============================================================
// 2026-08-06 (tarde) — O QUE O SDK PAROU DE MANDAR, E POR QUÊ
// ============================================================
// `detectors[]` virou DELTA: só sobe quem atrasou ou segurou a fila. E cada
// entrada perdeu `cadence_ms` e `runs`. Motivo: os dois juntos publicavam, uma
// vez por minuto e em texto claro, QUAIS checagens existem e DE QUANTO EM
// QUANTO TEMPO cada uma roda — o mapa da nossa capacidade de detecção na mão de
// quem lê o tráfego. (`runs / window_s` devolve a cadência por divisão, então
// tirar só um dos dois não teria adiantado.)
//
// AS TRÊS CONSEQUÊNCIAS PRA ESTA TELA, todas tratadas e nenhuma silenciosa:
//
//   1. "Ninguém atrasou" passou a produzir `detectors: []` — igualzinho a
//      "este SDK não sabe atribuir". O que os separa é `snapshots_attributed`,
//      que o backend agora deriva de um bit EXPLÍCITO do SDK (`can_attribute`)
//      em vez de deduzir da lista estar vazia. Sem esse bit, a frota mais sadia
//      possível seria narrada como a mais muda.
//   2. O critério "passou fome = 10× a cadência" não é mais calculável aqui.
//      Ele vem pronto do SDK, agregado em `sessions_starved`. O piso absoluto
//      de 30 s continua nesta tela — não precisa de cadência, e assim continua
//      mutável sem release de DLL.
//   3. O denominador por detector ("de X sessões, Y atrasaram") deixou de
//      existir por detector: com delta, X e Y convergem. O denominador honesto
//      é o `sessions_reporting` de FROTA, e é ele que a tela usa.
// ============================================================

/** Um detector, somado sobre todas as sessões da janela. */
export interface FleetDetectorHealth {
  detector_name: string;
  /**
   * Sessões em que ESTE detector teve problema na janela.
   *
   * ⚠️ Era `sessions_reporting` ("sessões que reportaram este detector"). O
   * nome mudou porque o significado mudou: com formato delta, um detector só
   * aparece quando teve problema, então o valor antigo passaria a ser sempre
   * igual a `sessions_late ∪ sessions_slow` e a coluna "N de M" imprimiria
   * eternamente "N de N". O M honesto é `FleetSchedulerHealth.sessions_reporting`.
   */
  sessions_affected: number;
  /** Destas, em quantas o próprio SDK classificou como fome (>= 10× a cadência dele). */
  sessions_starved: number;
  /** Destas, quantas viram este detector vencer e rodar fora da tolerância. */
  sessions_late: number;
  /** Destas, quantas o viram segurar a fila (tick longo). */
  sessions_slow: number;
  late_runs: number;
  worst_lateness_ms: number;
  slow_runs: number;
  worst_run_ms: number;
}

/**
 * Contadores do registry INTEIRO, sem atribuição por detector.
 *
 * É o que o DetectorRegistry consegue produzir hoje: `late_runs()`,
 * `max_lateness_ms()`, `slow_runs()` e `max_run_ms()` são agregados do registry,
 * não por detector. Existem aqui para que a tela possa dizer "houve atraso, mas
 * esta build não diz de quem" em vez de inventar um culpado.
 */
export interface UnattributedTotals {
  late_runs: number;
  max_lateness_ms: number;
  slow_runs: number;
  max_run_ms: number;
}

/**
 * ⚠️ Este tipo descreve o CONTRATO ideal — não uma garantia de runtime.
 *
 * `painel/src/lib/detectorHealth.ts` (`veredicto()`) NÃO confia cegamente
 * nestes campos como `number`: ele valida cada um em runtime antes de decidir
 * "saudável" vs "problema" vs "não sabemos". O motivo é que um backend
 * antigo, um proxy no meio do caminho, ou uma resposta parcial durante um
 * rollout (como o desta própria feature — a migration que a viabiliza ainda
 * não está commitada neste repo) podem entregar um objeto sem `snapshots`,
 * `snapshots_reporting` ou `snapshots_attributed`, e o TypeScript não pega
 * isso: ele confia no `resp.data` do axios sem validar a forma. Se algum dia
 * este serviço ganhar validação de schema em runtime, os dois lados (aqui e
 * em `detectorHealth.ts`) precisam ser revistos juntos.
 */
export interface FleetSchedulerHealth {
  window_minutes: number;
  /** Snapshots na janela, reportando escalonamento ou não. */
  snapshots: number;
  /** Destes, quantos trouxeram o bloco de escalonamento. */
  snapshots_reporting: number;
  /**
   * Destes, quantos vieram de um SDK que SABE atribuir por detector.
   *
   * ⚠️ SIGNIFICADO REDEFINIDO em 2026-08-06 (tarde): era "quantos trouxeram
   * breakdown NÃO-VAZIO". Com formato delta um snapshot perfeitamente saudável
   * tem `detectors: []` e continua sabendo atribuir — pela leitura antiga ele
   * cairia no balde dos "sem detalhamento" e esta tela narraria
   * "N snapshot(s) sem detalhamento por detector" sobre uma frota que detalha.
   * É esta redefinição que faz `reporting - attributed` continuar significando
   * o que o texto do veredito diz que significa.
   */
  snapshots_attributed: number;
  sessions: number;
  sessions_reporting: number;
  unattributed: UnattributedTotals;
  detectors: FleetDetectorHealth[];
}

/**
 * Bloco por SESSÃO, como persistido (vem em AggregatedDiagnostic).
 * `{}` = aquela sessão rodava um SDK que ainda não reporta.
 */
export interface SessionSchedulerHealth {
  window_s?: number;
  /**
   * "Este SDK sabe dizer de qual detector" — CAPACIDADE, não presença de
   * linhas. `attributed: true` com `detectors: []` significa "ninguém atrasou";
   * `attributed: false` com `detectors: []` significa "não sabemos de quem".
   * A tela por sessão TEM de ler os dois campos juntos.
   */
  attributed?: boolean;
  detectors?: Array<{
    name: string;
    late_runs: number;
    max_lateness_ms: number;
    slow_runs: number;
    max_run_ms: number;
    /** Veredito de fome do próprio SDK (cegueira >= 10× a cadência dele). */
    starved?: boolean;
  }>;
  totals?: UnattributedTotals;
}

export async function getFleetSchedulerHealth(
  windowMinutes = 60,
): Promise<FleetSchedulerHealth> {
  const resp = await api.get<FleetSchedulerHealth>('/admin/scheduler-health', {
    params: { window_minutes: windowMinutes },
  });
  return resp.data;
}
